import { FormEvent, useState } from "react";
import { Button, Col, Form, Input, Label, Row, Spinner } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "./style.module.scss";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { CreditnoteApi, CreditnoteDataType } from "@/api/CreditnoteApi";
import { DebitnoteApi, DebitnoteDataType } from "@/api/DebitnoteApi";
import { getFechaDeEmisionRFC3339 } from "@utils/EDocument";
import {
    ReferenceLinkedDocumentKind,
    isDocumentRejectedByHacienda,
    resolveDefaultTipoDocIRFromDocument,
} from "@utils/DocumentReferences";
import { InfoRefElement } from "@/atv/eInvoiceApi";

type PickerKind = "Invoice" | "Creditnote" | "Debitnote";

export type InternalDocumentResolvedMeta = {
    linkedDocumentId?: string;
    linkedDocumentKind: ReferenceLinkedDocumentKind;
    isRejectedByHacienda: boolean;
};

type InternalDocumentPickerProps = {
    onDocumentResolved: (info: InfoRefElement, meta: InternalDocumentResolvedMeta) => void;
    initialDocumentKind?: PickerKind;
};

type PickerSearchResult = {
    id: string;
    label: string;
    secondary: string;
    info: InfoRefElement;
    linkedDocumentId?: string;
    linkedDocumentKind: ReferenceLinkedDocumentKind;
    isRejectedByHacienda: boolean;
};

const PICKER_OPTIONS: { value: PickerKind; label: string }[] = [
    { value: "Invoice", label: "Factura / Tiquete" },
    { value: "Creditnote", label: "Nota de crédito" },
    { value: "Debitnote", label: "Nota de débito" },
];

const formatNumeroDocumento = (numero?: number | null): string =>
    numero != null ? `#${numero}` : "";

const buildInvoiceResult = (invoice: InvoiceDataType): PickerSearchResult => {
    const isRejected = isDocumentRejectedByHacienda(invoice.edocument_status_payload);
    return {
        id: `${invoice.id}`,
        label: `${formatNumeroDocumento(invoice.numero)} ${invoice.nombre_factura || ""}`.trim(),
        secondary: `${invoice.fecha || ""} · ${invoice.invoice_key || ""}`,
        info: {
            numero: `${invoice.invoice_key || ""}`,
            fechaEmisionUTC: getFechaDeEmisionRFC3339(invoice, "fecha"),
            tipoDoc: resolveDefaultTipoDocIRFromDocument({
                documentClass: "Invoice",
                einvoiceKind: invoice.einvoice_kind,
                edocumentStatusPayload: invoice.edocument_status_payload,
            }),
            codigo: "",
            razon: "",
        },
        linkedDocumentId: invoice.id,
        linkedDocumentKind: "Invoice",
        isRejectedByHacienda: isRejected,
    };
};

const buildCreditnoteResult = (note: CreditnoteDataType): PickerSearchResult => {
    const isRejected = isDocumentRejectedByHacienda(note.edocument_status_payload);
    return {
        id: `${note.id}`,
        label: `${formatNumeroDocumento(note.numero)} ${note.description || ""}`.trim(),
        secondary: `${note.created || ""} · ${note.document_key || ""}`,
        info: {
            numero: `${note.document_key || ""}`,
            fechaEmisionUTC: getFechaDeEmisionRFC3339(note, "created"),
            tipoDoc: resolveDefaultTipoDocIRFromDocument({
                documentClass: "Creditnote",
                edocumentStatusPayload: note.edocument_status_payload,
            }),
            codigo: "",
            razon: "",
        },
        linkedDocumentId: note.id,
        linkedDocumentKind: "Creditnote",
        isRejectedByHacienda: isRejected,
    };
};

const buildDebitnoteResult = (note: DebitnoteDataType): PickerSearchResult => {
    const isRejected = isDocumentRejectedByHacienda(note.edocument_status_payload);
    return {
        id: `${note.id}`,
        label: `${formatNumeroDocumento(note.numero)} ${note.description || ""}`.trim(),
        secondary: `${note.created || ""} · ${note.document_key || ""}`,
        info: {
            numero: `${note.document_key || ""}`,
            fechaEmisionUTC: getFechaDeEmisionRFC3339(note, "created"),
            tipoDoc: resolveDefaultTipoDocIRFromDocument({
                documentClass: "Debitnote",
                edocumentStatusPayload: note.edocument_status_payload,
            }),
            codigo: "",
            razon: "",
        },
        linkedDocumentId: note.id,
        linkedDocumentKind: "Debitnote",
        isRejectedByHacienda: isRejected,
    };
};

const InternalDocumentPicker = (props: InternalDocumentPickerProps) => {
    const { onDocumentResolved, initialDocumentKind = "Invoice" } = props;
    const [documentKind, setDocumentKind] = useState<PickerKind>(initialDocumentKind);
    const [inputValue, setInputValue] = useState<string>("");
    const [results, setResults] = useState<PickerSearchResult[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const runSearch = async (e: FormEvent) => {
        e.preventDefault();
        const numericValue = Number(inputValue);
        if (!inputValue || Number.isNaN(numericValue) || numericValue <= 0) {
            setErrorMessage("Ingrese un número de documento válido");
            setResults([]);
            return;
        }
        setErrorMessage("");
        setIsLoading(true);
        try {
            if (documentKind === "Invoice") {
                const { items } = await InvoiceApi.fetchAllByInvoiceNumber(numericValue);
                setResults(items.map(buildInvoiceResult));
            } else if (documentKind === "Creditnote") {
                const { items } = await CreditnoteApi.fetchAll({ numero: { eq: numericValue } });
                setResults(items.map(buildCreditnoteResult));
            } else {
                const { items } = await DebitnoteApi.fetchAll({ numero: { eq: numericValue } });
                setResults(items.map(buildDebitnoteResult));
            }
        } catch (err) {
            setErrorMessage("No fue posible buscar el documento");
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    const clearSearch = () => {
        setInputValue("");
        setResults([]);
        setErrorMessage("");
    };

    const handleSelect = (result: PickerSearchResult) => {
        onDocumentResolved(result.info, {
            linkedDocumentId: result.linkedDocumentId,
            linkedDocumentKind: result.linkedDocumentKind,
            isRejectedByHacienda: result.isRejectedByHacienda,
        });
        setResults([]);
        setInputValue("");
    };

    return (
        <div>
            <Row className="g-2 mb-2 align-items-end">
                <Col xs={12} md={5}>
                    <Label className={styles.fieldLabel} htmlFor="ref-picker-kind">
                        Buscar en
                    </Label>
                    <Input
                        id="ref-picker-kind"
                        type="select"
                        value={documentKind}
                        onChange={(e) => {
                            setDocumentKind(e.target.value as PickerKind);
                            setResults([]);
                        }}
                    >
                        {PICKER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Input>
                </Col>
                <Col xs={12} md={7}>
                    <Label className={styles.fieldLabel} htmlFor="ref-picker-numero">
                        Número
                    </Label>
                    <Form onSubmit={runSearch}>
                        <div className={styles.searchRow}>
                            <Input
                                id="ref-picker-numero"
                                className={styles.searchInput}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder="Número consecutivo"
                            />
                            <Button color="primary" type="submit" disabled={isLoading}>
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                            </Button>
                            <Button color="danger" type="button" onClick={clearSearch} disabled={isLoading}>
                                <FontAwesomeIcon icon={faXmark} />
                            </Button>
                        </div>
                    </Form>
                </Col>
            </Row>

            {errorMessage && <div className={styles.error}>{errorMessage}</div>}

            {isLoading && (
                <div className="text-center my-2">
                    <Spinner size="sm" /> Buscando...
                </div>
            )}

            {!isLoading && results.length > 0 && (
                <div className={styles.searchResults}>
                    {results.map((r) => (
                        <div key={r.id} className={styles.searchResultRow}>
                            <div className={styles.searchResultMeta}>
                                <span className={styles.searchResultPrimary}>{r.label}</span>
                                <span className={styles.searchResultSecondary}>{r.secondary}</span>
                            </div>
                            <Button
                                size="sm"
                                color="success"
                                type="button"
                                onClick={() => handleSelect(r)}
                            >
                                <FontAwesomeIcon icon={faCheck} /> Usar
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {!isLoading && results.length === 0 && inputValue && !errorMessage && (
                <div className={styles.fieldHint}>
                    Presione el botón de búsqueda para buscar el documento.
                </div>
            )}
        </div>
    );
};

export default InternalDocumentPicker;
